import {regexifyOriginFilters} from '../server/ws-client-verify.js';
import type {SecurityContext} from '../server/security/security-context.js';
import {pattern, ServerWebExchangeMatcher} from '../server/util/matchers.js';
import type {CorsConfig} from '../server/cors.js';
import type {Middleware} from '../server/types.ts';
import type {AsyncLocalStorage} from 'node:async_hooks';
import type {SocketRoute} from '../server/socket.js';
import {GatewayServer} from '../../gateway-server';
import type {
    ServerConfigurer,
    ServerConfigurerHandlerSpec,
    ServerConfigurerSocketSpec,
    ServerExchangeOptions,
    ServerWebExchange
} from '../../types/web/server';
import type {AuthorizationRule} from '../../types/auth';
import {IOGateway} from '@interopio/gateway';
export type PathPattern = string | RegExp;
export type RouteConfig = Readonly<{
    readonly storage: AsyncLocalStorage<{
        exchange: ServerWebExchange,
        securityContext?: Promise<SecurityContext>
    }>,
    readonly serverHeader?: string | false
    readonly corsConfig?: GatewayServer.ServerConfig['cors'],
    readonly authConfig?: GatewayServer.ServerConfig['auth'],
    readonly middleware: Middleware,
    readonly cors: Array<[PathPattern, CorsConfig | undefined]>,
    readonly authorize: Array<[ServerWebExchangeMatcher, AuthorizationRule]>
    readonly sockets: Map<string, SocketRoute>
}>

export async function configure(app: GatewayServer.ServerCustomizer, config: GatewayServer.ServerConfig, routes: RouteConfig): Promise<void> {
    const applyCors = (request: ServerConfigurerHandlerSpec['request'], options?: ServerExchangeOptions) => {
        // // disable CORS for this route
        // if (options?.cors === false) {
        //     const path = request.path as (PathPattern);
        //     routes.cors.push([request.path, undefined]);
        // }
        if (options?.cors) {
            const cors: CorsConfig = options.cors === true ? {
                allowOrigins: options.origins?.allow?.map(IOGateway.Filtering.regexify),
                allowMethods: request.method === undefined ? ['*'] : [request.method],
                allowCredentials: options.authorize?.access !== 'permitted' ? true : undefined,
            } : options.cors;
            const path = request.path as (PathPattern);
            routes.cors.push([path, cors]);
        }
    }

    const configurer = new class implements ServerConfigurer {

        handle(...handlers: Array<ServerConfigurerHandlerSpec>) {
            handlers.forEach(({request, options, handler}) => {
                const matcher = pattern(IOGateway.Filtering.regexify(request.path) as string | RegExp, {method: request.method});
                if (options?.authorize) {
                    routes.authorize.push([matcher, options.authorize]);
                }

                applyCors(request, options);
                const middleware = async (exchange: ServerWebExchange, next: () => Promise<void>): Promise<void> => {
                    const {match, variables} = await matcher(exchange);
                    if (match) {
                        await handler(exchange, variables);
                    } else {
                        await next();
                    }
                };
                routes.middleware.push(middleware);
            });
        }

        socket(...sockets: Array<ServerConfigurerSocketSpec>) {
            for (const {path, factory, options} of sockets) {
                const route = path ?? '/';

                // cors and authorize are currently handled for ws
                routes.sockets.set(route, {
                    default: path === undefined,
                    ping: options?.ping,
                    factory: factory,
                    maxConnections: options?.maxConnections,
                    authorize: options?.authorize,
                    originFilters: regexifyOriginFilters(options?.origins)
                });
            }
        }
    };
    await app(configurer, config);
}
